import { EmptyState, FlexColumn, Loading } from '@gorila-shared-ui/components';
import { Accordion, Panel } from 'baseui/accordion';
import { useEffect } from 'react';
import AutoSizer from 'react-virtualized-auto-sizer';
import { useRecoilState, useRecoilValue, useResetRecoilState, useSetRecoilState } from 'recoil';
import { useStyles } from '../../../hooks/useStyles';
import { getAlarmsReports } from '../../../services/reportsService';
import { apiParamsState, isAllWorkspacesSelect } from '../../../storage/app';
import {
  activeReportAlarmState,
  loadingAlarmsState,
  reportAlarmFilterState,
  reportAlarmsStates,
  reportEndDateState,
  reportSelectedAssetIdsState,
  reportStartDateState,
} from '../../../storage/reports';
import { HistoricAlarm } from '../../../types/currentState';
import AlarmsReportTableFromAsset from './AlarmsReportTableFromAsset';
import AlarmsReportTitle from './AlarmsReportTitle';

function AlarmsReports() {
  const { css } = useStyles();
  const selectedAssetIds = useRecoilValue(reportSelectedAssetIdsState);
  const endDate = useRecoilValue(reportEndDateState);
  const startDate = useRecoilValue(reportStartDateState);
  const apiParams = useRecoilValue(apiParamsState);
  const setActiveAlarm = useSetRecoilState(activeReportAlarmState);
  const resetActiveAlarm = useResetRecoilState(activeReportAlarmState);
  const [loadingAlarms, setLoadingAlarms] = useRecoilState(loadingAlarmsState);
  const setReportAlarms = useSetRecoilState(reportAlarmsStates);
  const resetReportAlarms = useResetRecoilState(reportAlarmsStates);
  const isAllWorkspacesSelected = useRecoilValue(isAllWorkspacesSelect);
  const alarmType = useRecoilValue(reportAlarmFilterState);

  useEffect(() => {
    return () => {
      resetReportAlarms();
    };
  }, []);

  useEffect(() => {
    if (!selectedAssetIds?.length || !startDate || !endDate || !apiParams) {
      resetActiveAlarm();
      return;
    }

    const promises = selectedAssetIds.map((assetId) =>
      getAlarmsReports(
        apiParams.clientId,
        assetId,
        startDate,
        endDate,
        isAllWorkspacesSelected ? undefined : apiParams.subClientId,
        alarmType,
      ),
    );

    const loadAlarms = async () => {
      setLoadingAlarms(true);
      const responses = await Promise.allSettled(promises);

      const newHistorics: { [key: string]: HistoricAlarm[] } = {};
      responses.forEach((result, index) => {
        if (result.status === 'fulfilled') {
          const {
            value: { alarmsReports, error },
          } = result;
          if (!error) {
            newHistorics[selectedAssetIds[index]] = alarmsReports ?? [];
          }
        }
      });
      setReportAlarms(newHistorics);
      if (newHistorics[selectedAssetIds[0]]) {
        setActiveAlarm(selectedAssetIds[0]);
      }
      setLoadingAlarms(false);
    };

    loadAlarms();
  }, [selectedAssetIds, startDate, endDate, alarmType, apiParams, isAllWorkspacesSelected]);

  if (loadingAlarms) {
    return <Loading />;
  }

  if (!selectedAssetIds?.length) {
    return <EmptyState title="Selecciona un activo para visualizar el reporte." />;
  }

  return (
    <FlexColumn
      gap={0}
      classNames={css({ flex: 1, overflow: 'hidden' })}
    >
      <AutoSizer>
        {({ width, height }) => (
          <div style={{ width, height, overflow: 'auto' }}>
            <Accordion
              key={selectedAssetIds[0]}
              initialState={{ expanded: [selectedAssetIds[0]] }}
              onChange={({ expanded }) => setActiveAlarm(expanded.length ? (expanded[0] as string) : undefined)}
            >
              {selectedAssetIds.map((assetId) => (
                <Panel
                  key={assetId}
                  title={<AlarmsReportTitle assetId={assetId} />}
                  overrides={{
                    Content: {
                      style: {
                        paddingTop: 0,
                      },
                    },
                  }}
                >
                  <AlarmsReportTableFromAsset assetId={assetId} />
                </Panel>
              ))}
            </Accordion>
          </div>
        )}
      </AutoSizer>
    </FlexColumn>
  );
}

export default AlarmsReports;
